/**
 * pastoralTensionDetector.js
 *
 * Detects named pastoral tensions present in a message.
 * A tension is a legitimate conflict between two valid pastoral values.
 *
 * DISTINCT from risk detection (ethics) which identifies danger.
 * Tensions are not dangerous — they are pastorally complex.
 *
 * Tensions:
 *   verdad_compasion        — truth vs remaining close and compassionate
 *   disciplina_restauracion — discipline process vs restorative spirit
 *   urgencia_paciencia      — act now vs wait for the right moment
 *   direccion_escucha       — give direction vs listen more first
 *   doctrina_acompañamiento — doctrinal precision vs human accompaniment
 *   confrontacion_silencio  — confront clearly vs remain quietly present
 *   conviccion_culpa        — pastoral conviction vs risk of inducing guilt
 */

const TENSION_DESCRIPTIONS = {
  verdad_compasion:        "Tensión entre decir la verdad y mantener cercanía compasiva.",
  disciplina_restauracion: "Tensión entre proceso disciplinario y espíritu restaurador.",
  urgencia_paciencia:      "Tensión entre actuar ahora y esperar el momento correcto.",
  direccion_escucha:       "Tensión entre dar dirección y necesitar escuchar más primero.",
  doctrina_acompañamiento: "Tensión entre precisión doctrinal y acompañamiento humano.",
  confrontacion_silencio:  "Tensión entre la necesidad de confrontar y la prudencia del silencio.",
  conviccion_culpa:        "Tensión entre convicción pastoral y riesgo de inducir culpa.",
};

/**
 * @param {{
 *   ethics:    object,   — from assessment.ethics
 *   emotion:   object|null,
 *   intent:    string,
 *   riskLevel: string,
 *   text:      string,
 * }} params
 * @returns {{
 *   hasTension:  boolean,
 *   primary:     string|null,
 *   secondary:   string|null,
 *   intensity:   "none"|"medium"|"high",
 *   instruction: string|null,
 * }}
 */
export function detectPastoralTension({ ethics, emotion, intent, riskLevel, text }) {
  const detected = ethics?.detected ?? [];
  const found    = [];

  // Discipline or apostasy present → disciplina_restauracion
  if (detected.includes("discipline") || detected.includes("apostasy")) {
    found.push("disciplina_restauracion");
  }

  // Shame + any risk → conviccion_culpa (conviction risks becoming guilt)
  if (
    emotion?.dominantEmotion === "shame" ||
    (riskLevel !== "none" && detected.includes("apostasy"))
  ) {
    found.push("conviccion_culpa");
  }

  // Conflict in family/visit context → direccion_escucha
  if (
    emotion?.dominantEmotion === "conflict" &&
    (intent === "family_followup" || intent === "visit_plan")
  ) {
    found.push("direccion_escucha");
  }

  // Spiritual confusion in doctrinal/reflective context → doctrina_acompañamiento
  if (
    emotion?.dominantEmotion === "spiritual_confusion" &&
    (intent === "reflection" || intent === "sermon" || intent === "prayer")
  ) {
    found.push("doctrina_acompañamiento");
  }

  // Grief + discipline/apostasy/marital → verdad_compasion
  if (
    (emotion?.dominantEmotion === "grief" ||
      riskLevel === "high" ||
      riskLevel === "critical") &&
    detected.some((d) => ["apostasy", "discipline", "marital_crisis"].includes(d))
  ) {
    found.push("verdad_compasion");
  }

  // Text: urgency words alongside patience/wait words → urgencia_paciencia
  if (
    /urgente|inmediatamente|ahora\s+mismo/i.test(text ?? "") &&
    /esperar|paciencia|tiempo|proceso/i.test(text ?? "")
  ) {
    found.push("urgencia_paciencia");
  }

  // Text: confrontation intent + emotional/risk context → confrontacion_silencio
  if (
    /confrontar|hablarle\s+(?:claro|directo)|decirle\s+la\s+verdad|correg/i.test(text ?? "") &&
    (emotion?.detected || riskLevel !== "none")
  ) {
    found.push("confrontacion_silencio");
  }

  if (found.length === 0) {
    return { hasTension: false, primary: null, secondary: null, intensity: "none", instruction: null };
  }

  const primary   = found[0];
  const secondary = found[1] ?? null;
  const intensity = found.length >= 2 ? "high" : "medium";

  return {
    hasTension: true,
    primary,
    secondary,
    intensity,
    instruction:
      `Reconoce la tensión: ${TENSION_DESCRIPTIONS[primary]} No la resuelvas prematuramente.`,
  };
}
